import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, MONTHS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const now = new Date();

export default function Reports() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data: branch } = useQuery({
    queryKey: ["rep-branch", year, month],
    queryFn: async () => (await api.get(`/reports/salary/branch-wise?year=${year}&month=${month}`)).data,
  });
  const { data: emp } = useQuery({
    queryKey: ["rep-emp", year, month],
    queryFn: async () => (await api.get(`/reports/salary/employee-wise?year=${year}&month=${month}`)).data,
  });

  const download = async (kind, format) => {
    try {
      const url = `/reports/salary/${kind}?year=${year}&month=${month}&format=${format}`;
      const resp = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${kind}_${year}_${month}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast.error("Download failed");
    }
  };

  const DownloadBtns = ({ kind }) => (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" data-testid={`${kind}-pdf`} onClick={() => download(kind, "pdf")}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
      <Button size="sm" variant="outline" data-testid={`${kind}-excel`} onClick={() => download(kind, "excel")}><FileDown className="h-4 w-4 mr-1" /> Excel</Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Salary Reports</h1>
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger data-testid="report-month-select" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="report-year-input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" />
        </div>
      </div>

      {/* Branch-wise */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-slate-900">Branch-wise Salary</h3>
          <DownloadBtns kind="branch-wise" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left px-3 py-2">Branch</th><th className="text-left px-3 py-2">Employees</th>
              <th className="text-left px-3 py-2">Net Payable</th><th className="text-left px-3 py-2">Paid</th><th className="text-left px-3 py-2">Pending</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(branch?.rows || []).length === 0 && <tr><td colSpan="5" className="px-3 py-4 text-slate-400">No data. Generate payroll first.</td></tr>}
              {(branch?.rows || []).map((r, i) => (
                <tr key={r.Branch ?? i} data-testid={`branch-report-row-${i}`}>
                  <td className="px-3 py-2 font-medium">{r.Branch}</td>
                  <td className="px-3 py-2">{r.Employees}</td>
                  <td className="px-3 py-2">{money(r["Net Payable"])}</td>
                  <td className="px-3 py-2 text-emerald-600">{money(r.Paid)}</td>
                  <td className="px-3 py-2 text-orange-600">{money(r.Pending)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee-wise */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-slate-900">Employee-wise Salary</h3>
          <DownloadBtns kind="employee-wise" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Branch</th>
              <th className="text-left px-3 py-2">P/H/A</th><th className="text-left px-3 py-2">Gross</th>
              <th className="text-left px-3 py-2">Net Salary</th><th className="text-left px-3 py-2">Reimb.</th>
              <th className="text-left px-3 py-2">Total Payable</th><th className="text-left px-3 py-2">Payment</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(emp?.rows || []).length === 0 && <tr><td colSpan="8" className="px-3 py-4 text-slate-400">No data. Generate payroll first.</td></tr>}
              {(emp?.rows || []).map((r, i) => (
                <tr key={r.Employee ?? i} data-testid={`emp-report-row-${i}`}>
                  <td className="px-3 py-2 font-medium">{r.Employee}</td>
                  <td className="px-3 py-2">{r.Branch}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.Present}/{r.Half}/{r.Absent}</td>
                  <td className="px-3 py-2">{money(r.Gross)}</td>
                  <td className="px-3 py-2">{money(r["Net Salary"])}</td>
                  <td className="px-3 py-2 text-blue-600">{money(r.Reimbursement)}</td>
                  <td className="px-3 py-2 font-semibold">{money(r["Total Payable"])}</td>
                  <td className="px-3 py-2">{r.Payment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
